import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { getStoreCatalogue } from '../api/stockLedger';
import { inputSx } from '../theme';

const DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;

function normalizeCatalogueItems(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data?.items)) return data.data.items;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

export function catalogueAvgMrp(item) {
  const batches = item?.batches || [];
  const mrps = batches
    .map(b => Number(b.mrp))
    .filter(n => Number.isFinite(n) && n >= 0);
  if (!mrps.length) return null;
  const avg = mrps.reduce((sum, n) => sum + n, 0) / mrps.length;
  return avg.toFixed(2);
}

export function catalogueItemLabel(item) {
  if (!item) return '';
  const name = item.item_name || item.item_code || 'Item';
  const code = item.item_code ? ` (${item.item_code})` : '';
  const uomCode = item.base_uom_code || item.uom_code || '';
  const avail = item.total_available_quantity != null
    ? ` · Avail: ${item.total_available_quantity}${uomCode ? ` ${uomCode}` : ''}`
    : '';
  const avgMrp = catalogueAvgMrp(item);
  const mrp = avgMrp != null ? ` · Avg. MRP: ${avgMrp}` : '';
  return `${name}${code}${avail}${mrp}`;
}

/** @deprecated use catalogueItemLabel */
export const stockBalanceLabel = catalogueItemLabel;

function itemKey(item) {
  return item?.item_id || '';
}

function catalogueItemClassId(item) {
  return String(
    item?.item_class_id
    || item?.item_class?.item_class_id
    || item?.item_class?.id
    || '',
  );
}

function filterByAllowedItemClasses(items, isRestricted, allowedIds) {
  if (!isRestricted) return items;
  const allowed = new Set((allowedIds || []).map(String).filter(Boolean));
  if (!allowed.size) return [];
  return items.filter((item) => allowed.has(catalogueItemClassId(item)));
}

/** Debounced store-catalogue search — min 2 chars, item-level options with total availability. */
export default function StockBalanceAutocomplete({
  storeId,
  value,
  onChange,
  label,
  placeholder = 'Search item…',
  disabled = false,
  inputRef,
  onKeyDown,
  sx,
  itemClassRestricted = false,
  allowedItemClassIds,
  itemClassesLoading = false,
  includeExpired = false,
}) {
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [open, setOpen] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(inputValue.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue]);

  useEffect(() => {
    if (!value) {
      setSelectedItem(null);
      return;
    }
    if (selectedItem && itemKey(selectedItem) === value) return;
    const found = options.find(item => itemKey(item) === value) || null;
    if (found) setSelectedItem(found);
  }, [value, options, selectedItem]);

  const isSearching = inputValue.trim().length >= MIN_SEARCH_LENGTH;
  const allowedClassKey = itemClassRestricted
    ? (allowedItemClassIds || []).map(String).filter(Boolean).join(',')
    : '';

  useEffect(() => {
    if (!open || !storeId) return;
    // Hold until the destination allow-list resolves so a restricted store
    // never flashes items it cannot receive.
    if (itemClassesLoading) return;

    const inputQuery = inputValue.trim();
    const query = debouncedSearch;
    const shouldSearch = query.length >= MIN_SEARCH_LENGTH;
    const pendingSearch = inputQuery.length >= MIN_SEARCH_LENGTH && inputQuery !== query;

    if (pendingSearch) {
      setOptions([]);
      return;
    }

    if (!shouldSearch) {
      setOptions([]);
      return;
    }

    const requestId = ++requestIdRef.current;

    async function fetchCatalogue() {
      setOptions([]);
      setLoading(true);
      try {
        // API: GET /inventory/stock/store-catalogue/
        const data = await getStoreCatalogue({
          store_id: storeId,
          search: query,
          exclude_expired: !includeExpired,
          include_uom_name: true,
        });
        if (requestId !== requestIdRef.current) return;
        setOptions(normalizeCatalogueItems(data));
      } catch {
        if (requestId === requestIdRef.current) setOptions([]);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    }

    fetchCatalogue();
  }, [debouncedSearch, open, storeId, inputValue, itemClassesLoading, includeExpired]);

  const classFilteredOptions = useMemo(
    () => filterByAllowedItemClasses(options, itemClassRestricted, allowedClassKey.split(',')),
    [options, itemClassRestricted, allowedClassKey],
  );

  const displayOptions = useMemo(() => {
    if (isSearching || !selectedItem) return classFilteredOptions;
    const key = itemKey(selectedItem);
    if (classFilteredOptions.some(item => itemKey(item) === key)) return classFilteredOptions;
    if (itemClassRestricted) {
      const allowed = new Set(allowedClassKey.split(',').filter(Boolean));
      if (!allowed.has(catalogueItemClassId(selectedItem))) return classFilteredOptions;
    }
    return [selectedItem, ...classFilteredOptions];
  }, [classFilteredOptions, selectedItem, isSearching, itemClassRestricted, allowedClassKey]);

  const noOptionsText = (() => {
    if (!storeId) return 'Select a store first';
    if (itemClassesLoading) return 'Loading allowed item classes…';
    if (loading) return 'Searching…';
    if (inputValue.trim().length < MIN_SEARCH_LENGTH) {
      return `Type at least ${MIN_SEARCH_LENGTH} characters`;
    }
    if (itemClassRestricted) return 'No compatible stock for the destination store';
    return 'No stock found';
  })();

  return (
    <Autocomplete
      size="small"
      fullWidth
      disabled={disabled || !storeId || itemClassesLoading}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={displayOptions}
      loading={loading}
      value={selectedItem}
      inputValue={inputValue}
      onInputChange={(_, newInput, reason) => {
        if (reason === 'input' || reason === 'clear' || reason === 'reset') {
          setInputValue(newInput);
          if (
            (reason === 'input' || reason === 'clear')
            && selectedItem
            && newInput.trim() !== catalogueItemLabel(selectedItem)
          ) {
            setSelectedItem(null);
            onChange('', null);
          }
        }
      }}
      onChange={(_, item) => {
        if (item && itemClassRestricted) {
          const allowed = new Set(allowedClassKey.split(',').filter(Boolean));
          if (!allowed.has(catalogueItemClassId(item))) {
            setSelectedItem(null);
            onChange('', null);
            return;
          }
        }
        setSelectedItem(item);
        onChange(item ? itemKey(item) : '', item);
      }}
      getOptionLabel={catalogueItemLabel}
      isOptionEqualToValue={(a, b) => itemKey(a) === itemKey(b)}
      filterOptions={(x) => x}
      noOptionsText={noOptionsText}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          inputRef={inputRef}
          onKeyDown={onKeyDown}
          InputLabelProps={label ? { shrink: true } : undefined}
          sx={{ ...inputSx, ...sx }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
